import { create } from 'zustand';
import { supabase } from '../lib/supabase';

interface User {
  id: string;
  email?: string;
  username?: string;
  role?: 'free' | 'premium' | 'creator' | 'admin';
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  isAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        const { data: userData } = await supabase
          .from('users_data')
          .select('username, role')
          .eq('id', data.user.id)
          .single();

        set({
          user: {
            id: data.user.id,
            email: data.user.email,
            username: userData?.username,
            role: userData?.role
          },
          error: null
        });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to sign in' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signUp: async (email: string, password: string, username: string) => {
    set({ loading: true, error: null });
    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (signUpError) throw signUpError;

      if (data.user) {
        const { error: profileError } = await supabase
          .from('users_data')
          .insert([{ 
            id: data.user.id, 
            username,
            role: 'free'
          }]);

        if (profileError) throw profileError;

        set({
          user: {
            id: data.user.id,
            email: data.user.email,
            username,
            role: 'free'
          },
          error: null
        });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to sign up' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      set({ user: null, error: null });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to sign out' });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  checkAuth: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // For Google sign-in, we need to create a user_data entry if it doesn't exist
        const { data: existingUserData } = await supabase
          .from('users_data')
          .select('username, role')
          .eq('id', session.user.id)
          .single();

        if (!existingUserData) {
          // Create new user_data entry for Google users
          const username = session.user.email?.split('@')[0] || 'user';
          await supabase
            .from('users_data')
            .insert([{
              id: session.user.id,
              username: username,
              role: 'free'
            }]);

          set({
            user: {
              id: session.user.id,
              email: session.user.email,
              username: username,
              role: 'free'
            },
            error: null
          });
        } else {
          set({
            user: {
              id: session.user.id,
              email: session.user.email,
              username: existingUserData.username,
              role: existingUserData.role
            },
            error: null
          });
        }
      } else {
        set({ user: null });
      }
    } catch (error) {
      console.error('Auth check error:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to check auth status' });
      set({ user: null });
    } finally {
      set({ loading: false });
    }
  },

  isAdmin: () => {
    return get().user?.role === 'admin';
  }
}));